import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Flex,
  HStack,
  IconButton,
  Link,
  Stack,
  useDisclosure,
  Text
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

const flagImages = [
  { src: "/greek-flag.png", alt: "Greek Flag", lang: "el" },
  { src: "/uk-flag.png", alt: "UK Flag", lang: "en" },
];

function LanguageSwitcher({ i18n }) {
  const [activeIdx, setActiveIdx] = useState(i18n.language === "el" ? 0 : 1);

  const handleSwipe = () => {
    const nextIdx = (activeIdx + 1) % flagImages.length;
    setActiveIdx(nextIdx);
    i18n.changeLanguage(flagImages[nextIdx].lang);
  };

  const labels = ["Ελληνικά", "English"];

  return (
    <HStack spacing={3}>
      <Box
        as="button"
        aria-label="Change language"
        borderRadius="full"
        boxSize={8}
        bg="whiteAlpha.200"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="2px solid #0d5eaf"
        overflow="hidden"
        position="relative"
        onClick={handleSwipe}
        _hover={{ boxShadow: "0 0 0 2px #0d5eaf" }}
        transition="all 0.2s"
      >
        <Box
          as="img"
          src={flagImages[activeIdx].src}
          alt={flagImages[activeIdx].alt}
          boxSize={8}
          borderRadius="full"
          objectFit="cover"
          transition="all 0.3s"
        />
      </Box>
      <Text color="white" fontWeight="bold" fontSize="sm">
        {labels[activeIdx]}
      </Text>
    </HStack>
  );
}

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [active, setActive] = useState("home");
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = useMemo(
    () => [
      { name: "home", id: "home" },
      { name: "about", id: "about" },
      { name: "events", id: "events" },
      { name: "contact", id: "contact" },
      { name: "team", id: "team" },
    ],
    []
  );

  useEffect(() => {
    const els = links.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { root: null, rootMargin: "-20% 0px -60% 0px", threshold: [0.1, 0.25, 0.5] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [links]);

  const goTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActive(id);
      if (isOpen) onClose();
    }
  };

  const linkStyle = (id) => ({
    fontWeight: "bold",
    color: "white",
    px: 3,
    py: 2,
    borderRadius: "md",
    transition: "all .2s ease",
    bg: active === id ? "whiteAlpha.200" : "transparent",
    _hover: { bg: "whiteAlpha.300" },
  });

  return (
    <>
      <Box
        position="fixed"
        top="0"
        left="0"
        w="100%"
        zIndex="100"
        bgGradient={scrolled ? undefined : "linear(to-b, blackAlpha.700, transparent)"}
        bg={scrolled ? "blackAlpha.800" : undefined}
        boxShadow={scrolled ? "0 6px 20px rgba(0,0,0,0.25)" : "none"}
      >
        <Container maxW="container.xl" px={{ base: 4, md: 6, lg: 8 }}>
          <Flex
            h={{ base: 16, md: 20, lg: 20 }}
            align="center"
            justify="space-between"
            color="white"
          >
            {/* Logo */}
            <Link href="#home" onClick={goTo("home")} _hover={{ textDecoration: "none" }}>
              <Box
                as="img"
                src="/logo-arena.png"
                alt="Arena"
                h={{ base: "36px", md: "44px", lg: "52px" }}
                cursor="pointer"
                transition="transform 0.2s ease-in-out"
                _hover={{ transform: "scale(1.06)" }}
              />
            </Link>

            {/* Desktop links */}
            <HStack spacing={{ base: 4, md: 8, lg: 10 }} display={{ base: "none", md: "flex" }}>
              {links.map((l) => (
                <Text
                  key={l.id}
                  as="button"
                  onClick={goTo(l.id)}
                  {...linkStyle(l.id)}
                  fontSize={{ base: "xs", md: "sm", lg: "md" }}
                >
                  {t(l.name)}
                </Text>
              ))}
              <LanguageSwitcher i18n={i18n} />
            </HStack>

            {/* Burger (mobile) */}
            <IconButton
              size="md"
              icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
              aria-label="Toggle Menu"
              display={{ md: "none" }}
              onClick={isOpen ? onClose : onOpen}
              color="white"
              bg="transparent"
              _hover={{ bg: "whiteAlpha.100" }}
            />
          </Flex>
        </Container>
      </Box>

      {/* MOBILE OVERLAY + SIDEBAR */}
      <Box
        position="fixed"
        top="0"
        left="0"
        w="100vw"
        h="100vh"
        zIndex="1000"
        display={{ base: "block", md: "none" }}
        pointerEvents={isOpen ? "auto" : "none"}
      >
        {/* Overlay (click-outside) */}
        <Box
          position="absolute"
          inset="0"
          bg="blackAlpha.600"
          opacity={isOpen ? 1 : 0}
          transition="opacity 0.3s ease-in-out"
          onClick={onClose}
        />
        {/* Sidebar */}
        <Box
          position="absolute"
          top="0"
          left="0"
          h="100vh"
          w="270px"
          bg="blackAlpha.800"
          color="white"
          p={6}
          transform={isOpen ? "translateX(0)" : "translateX(-100%)"}
          transition="transform 0.3s ease-in-out"
        >
          <Stack spacing={6} mt={10}>
            {links.map((l) => (
              <Text
                key={l.id}
                as="button"
                onClick={goTo(l.id)}
                {...linkStyle(l.id)}
                fontSize="md"
              >
                {t(l.name)}
              </Text>
            ))}
            <LanguageSwitcher i18n={i18n} />
          </Stack>
        </Box>
      </Box>
    </>
  );
}
